// ============================================
// src/serverApi.js
// Talks to the Vibely payment verification server.
// The server is the ONLY thing that credits coins /
// activates subscriptions - the browser just asks it to
// verify a payment that already happened.
// ============================================

import { getAuth } from 'firebase/auth';

// ⚠️ Set this to YOUR Railway URL (the one you tested /health on)
const SERVER_URL = 'https://vibely-server-production-8a60.up.railway.app';

// Helper: get the current user's Firebase ID token
async function getIdToken() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not logged in');
  return await user.getIdToken();
}

// ── Verify a coin purchase and credit coins ──────────────
export const verifyCoinsPurchase = async (reference, bundleId, provider = 'paystack') => {
  try {
    const token = await getIdToken();
    const resp = await fetch(`${SERVER_URL}/verify-coins`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify({ reference, bundleId, provider })
    });
    const data = await resp.json();
    if (!resp.ok) {
      return { success: false, error: data.error || 'Verification failed' };
    }
    return data; // { success: true, coinsAdded, newBalance }
  } catch (e) {
    return { success: false, error: e.message };
  }
};


// ============================================
// ADD THESE to src/serverApi.js
// (paste before the final closing, alongside the existing functions)
// ============================================

// ── Claim a book (server deducts author coins) ───────────
export const claimBookServer = async (bookId) => {
  try {
    const token = await getIdToken();
    const resp = await fetch(`${SERVER_URL}/claim-book`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify({ bookId })
    });
    const data = await resp.json();
    if (!resp.ok) return { success: false, error: data.error || 'Claim failed' };
    return data; // { success, claimId, lockId, deadline, newAuthorBalance }
  } catch (e) {
    return { success: false, error: e.message };
  }
};

// ── Approve a review (admin only, server pays reviewer) ──
export const approveReviewServer = async (authorId, reviewerId, bookId, coinsAmount, bonusCoins = 0) => {
  try {
    const token = await getIdToken();
    const resp = await fetch(`${SERVER_URL}/approve-review`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify({ authorId, reviewerId, bookId, coinsAmount, bonusCoins })
    });
    const data = await resp.json();
    if (!resp.ok) return { success: false, error: data.error || 'Approve failed' };
    return data; // { success, newReviewerBalance, newAuthorBalance }
  } catch (e) {
    return { success: false, error: e.message };
  }
};

// ── Reverse expired claims (server refunds authors) ──────
export const reverseExpiredClaimsServer = async () => {
  try {
    const token = await getIdToken();
    const resp = await fetch(`${SERVER_URL}/reverse-expired-claims`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      }
    });
    const data = await resp.json();
    if (!resp.ok) return { success: false, error: data.error || 'Reverse failed' };
    return data; // { success, reversedCount }
  } catch (e) {
    return { success: false, error: e.message };
  }
};
// ── Verify a subscription payment and activate it ────────
export const verifySubscription = async (reference, planKey, expectedKobo, provider = 'paystack') => {
  try {
    const token = await getIdToken();
    const resp = await fetch(`${SERVER_URL}/verify-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify({ reference, planKey, provider })
    });
    const data = await resp.json();
    if (!resp.ok) return { success: false, error: data.error || 'Verification failed' };
    return data; // { success: true, plan, expiresAt }
  } catch (e) {
    return { success: false, error: e.message };
  }
};

// ── Ban / unban a user (admin only, server-verified) ─────
export const banUserServer = async (targetUserId, banned) => {
  try {
    const token = await getIdToken();
    const resp = await fetch(`${SERVER_URL}/ban-user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
      body: JSON.stringify({ targetUserId, banned })
    });
    const data = await resp.json();
    if (!resp.ok) return { success: false, error: data.error || 'Ban failed' };
    return data;
  } catch (e) { return { success: false, error: e.message }; }
};

// ── Request a review (server deducts coins + creates the book copy) ──
export const requestReviewServer = async (masterBookId, readerType, coins, deadlineDays) => {
  try {
    const token = await getIdToken();
    const resp = await fetch(`${SERVER_URL}/request-review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
      body: JSON.stringify({ masterBookId, readerType, coins, deadlineDays })
    });
    const data = await resp.json();
    if (!resp.ok) return { success: false, error: data.error || 'Request failed' };
    return data;
  } catch (e) { return { success: false, error: e.message }; }
};

// ── Submit a review (server sets reviewerId, flags underReview, cleans up) ──
export const submitReviewServer = async (reviewData) => {
  try {
    const token = await getIdToken();
    const resp = await fetch(`${SERVER_URL}/submit-review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
      body: JSON.stringify(reviewData)
    });
    const data = await resp.json();
    if (!resp.ok) return { success: false, error: data.error || 'Submit failed' };
    return data;
  } catch (e) { return { success: false, error: e.message }; }
};
